<template>
  <form
    :class="hideHeader ? 'form-stack' : 'form-stack px-4 py-8 sm:px-8 bg-white'"
    @submit.prevent="submitFundraiser"
  >
    <div v-if="!hideHeader">
      <h3 class="form-section-title">Add New Fundraiser</h3>
    </div>
    <fieldset class="form-grid">
      <InputField
        :labelSize="'small'"
        :inputTextSize="'small'"
        v-model:value="form.firstName.value"
        :errors="form.firstName.errors"
        @blur="form.firstName.validate()"
        label="First name"
        type="text"
        text="fundraiser-first-name"
        id="fundraiser-first-name"
        placeholder="Enter first name..."
      />
      <InputField
        :labelSize="'small'"
        :inputTextSize="'small'"
        v-model:value="form.lastName.value"
        :errors="form.lastName.errors"
        @blur="form.lastName.validate()"
        label="Last name"
        type="text"
        text="fundraiser-last-name"
        id="fundraiser-last-name"
        placeholder="Enter last name..."
      />
      <InputField
        :labelSize="'small'"
        :inputTextSize="'small'"
        v-model:value="form.employeeId.value"
        :errors="form.employeeId.errors"
        @blur="form.employeeId.validate()"
        label="Employee ID"
        type="text"
        text="fundraiser-employee_id"
        id="fundraiser-employee_id"
        placeholder="Enter employee id..."
      />
      <InputField
        :labelSize="'small'"
        :inputTextSize="'small'"
        v-model:value="form.email.value"
        :errors="form.email.errors"
        @blur="form.email.validate()"
        label="Email"
        type="email"
        text="fundraiser-email"
        id="fundraiser-email"
        placeholder="example@email.com"
        autocomplete="email"
      />
      <div class="form-field-full">
        <label class="input--label--small">Profile Picture</label>
        <input
          id="profilePicture"
          type="file"
          accept="image/*"
          @change="onFileChange"
          class="mt-1 block w-full text-sm text-gray-500 file:mr-3 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-primaryDark hover:file:bg-primary/20"
        />
      </div>
    </fieldset>

    <section v-if="isSuperCanvassingOrgAdmin" class="form-section form-section-divider">
      <label for="canvassingOrg" class="input--label--small">Canvassing Organization</label>
      <Multiselect
        id="fundraiser-canvassingOrg"
        v-model="selectedCanvassingOrg"
        :options="accessibleCanvassingOrgs"
        :multiple="false"
        :close-on-select="true"
        :clear-on-select="false"
        :preserve-search="false"
        placeholder="Select canvassing organization"
        label="name"
        track-by="id"
        class="mt-1 block w-full"
      />
      <p v-if="canvassingOrgError" class="mt-1 text-sm text-red-600">{{ canvassingOrgError }}</p>
      <p class="mt-1 text-xs text-gray-500">
        Choose which organization this fundraiser will belong to.
      </p>
    </section>

    <section class="form-section form-section-divider">
      <label for="homeOffice" class="input--label--small">Home Office</label>
      <HomeOfficeSelect
        input-id="fundraiser-homeOffice"
        :modelValue="form.homeOffice.value"
        @update:modelValue="form.homeOffice.value = $event"
        placeholder="Select Home Office"
        input-class="mt-1 block w-full"
        :errors="form.homeOffice.errors"
        :initial-options="initialHomeOffices"
        :initial-has-more="initialHomeOfficesHasMore"
      />
    </section>
    <div class="form-actions">
      <BaseButton v-if="hideHeader" type="button" variant="secondary" @click="$emit('cancel')">
        Cancel
      </BaseButton>
      <BaseButton type="submit" id="fundraiser-info-submit">Submit</BaseButton>
    </div>
  </form>
</template>

<script>
import { ref, watch, computed } from 'vue'
import { useStore } from 'vuex'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import InputField from '@/components/inputs/InputField'
import HomeOfficeSelect from '@/components/HomeOfficeSelect.vue'
import { InviteFundraiserForm } from '@/services/users/'
import { base64FromFile } from '@/utils/imageHelpers'

export default {
  name: 'FundraiserFormInfo',
  components: {
    InputField,
    HomeOfficeSelect,
    Multiselect,
  },
  props: {
    clearForm: {
      type: Boolean,
      default: false,
    },
    initialHomeOffices: {
      type: Array,
      default: () => [],
    },
    initialHomeOfficesHasMore: {
      type: Boolean,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submitForm', 'cancel'],
  setup(props, { emit }) {
    const store = useStore()
    const form = ref(new InviteFundraiserForm())
    const selectedCanvassingOrg = ref(null)
    const canvassingOrgError = ref('')

    const isSuperCanvassingOrgAdmin = computed(() => store.getters.isSuperCanvassingOrgAdmin)
    const accessibleCanvassingOrgs = computed(() => store.getters.accessibleCanvassingOrgs || [])

    watch(selectedCanvassingOrg, (org) => {
      form.value.canvassingOrg.value = org?.id || null
      if (org?.id) {
        canvassingOrgError.value = ''
      }
    })

    const onFileChange = async (e) => {
      const file = e.target.files[0]
      if (!file) {
        form.value.profilePicture.value = null
        form.value.profilePicture.errors = []
        return
      }
      try {
        const base64 = await base64FromFile(file)
        form.value.profilePicture.value = base64
        form.value.profilePicture.errors = []
      } catch (err) {
        form.value.profilePicture.errors = [{ message: 'Invalid image file.' }]
      }
    }

    watch(() => {
      if (props.clearForm) {
        form.value.firstName.value = ''
        form.value.firstName.errors = []
        form.value.lastName.value = ''
        form.value.lastName.errors = []
        form.value.employeeId.value = ''
        form.value.employeeId.errors = []
        form.value.email.value = ''
        form.value.email.errors = []
        form.value.homeOffice.value = null
        form.value.homeOffice.errors = []
        form.value.profilePicture.value = null
        form.value.profilePicture.errors = []
        form.value.canvassingOrg.value = null
        form.value.canvassingOrg.errors = []
        selectedCanvassingOrg.value = null
        canvassingOrgError.value = ''
      }
    })

    const submitFundraiser = () => {
      const unwrappedForm = form.value
      unwrappedForm.validate()

      if (isSuperCanvassingOrgAdmin.value && !unwrappedForm.canvassingOrg.value) {
        canvassingOrgError.value = 'Please select a canvassing organization.'
        return
      }

      if (!unwrappedForm.isValid) return

      emit('submitForm', form.value.formVal)
    }

    return {
      form,
      submitFundraiser,
      onFileChange,
      isSuperCanvassingOrgAdmin,
      accessibleCanvassingOrgs,
      selectedCanvassingOrg,
      canvassingOrgError,
    }
  },
}
</script>
